import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { useStudentAuth } from '@/context/StudentAuthContext';
import { usePageContent, getSection } from '@/hooks/usePageContent';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import SEO from '@/components/SEO';
import {
  MapPin,
  Globe,
  Users,
  Trophy,
  GraduationCap,
  DollarSign,
  Calendar,
  FileText,
  ChevronRight,
  ChevronDown,
  MessageCircle,
  Phone,
  Star,
  Clock,
  BookOpen,
  Briefcase,
  Home,
  CheckCircle2,
  Loader2,
  ArrowLeft,
  Bookmark,
  BookmarkCheck,
  ExternalLink,
  Sparkles
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { LeadCaptureForm } from '@/components/LeadCaptureForm';
import { toast } from 'sonner';

const API_URL = window.location.origin;

const UniversityDetailPage = () => {
  const { slug } = useParams();
  const { isDark } = useTheme();
  const { user } = useStudentAuth();
  const [university, setUniversity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [relatedUniversities, setRelatedUniversities] = useState([]);
  const [isSaved, setIsSaved] = useState(false);
  const [savingState, setSavingState] = useState(false);

  // CMS Template Strings (Phase D - shared detail template)
  const { content: cmsContent } = usePageContent('university-detail');
  const t = getSection(cmsContent, 'template_strings', {
    overview_header: 'Overview',
    why_study_prefix: 'Why Study at',
    programs_header: 'Popular Programs',
    requirements_header: 'Admission Requirements',
    campus_header: 'Campus Life',
    career_header: 'Career Outcomes',
    faq_header: 'Frequently Asked Questions',
    related_header: 'Related Universities',
    world_ranking_label: 'World Ranking',
    qs_year_label: 'QS 2027',
    acceptance_rate_label: 'Acceptance Rate',
    annual_tuition_label: 'Annual Tuition',
    apply_now_button: 'Apply Now',
    back_label: 'Back to Universities'
  });

  useEffect(() => {
    const fetchUniversity = async () => {
      try {
        const res = await fetch(`${API_URL}/api/university-pages/public/${slug}`);
        if (res.ok) {
          const data = await res.json();
          setUniversity(data);
          
          // Fetch related universities for SEO internal linking
          try {
            const relatedRes = await fetch(`${API_URL}/api/university-pages/public/${slug}/related?limit=6`);
            if (relatedRes.ok) {
              const relatedData = await relatedRes.json();
              setRelatedUniversities(relatedData.related_universities || []);
            }
          } catch (e) {
            console.log('Related universities not available');
          }
        } else if (res.status === 404) {
          setError('University not found');
        } else {
          setError('Failed to load university details');
        }
      } catch (e) {
        console.error('Error fetching university:', e);
        setError('Failed to load university details');
      } finally {
        setLoading(false);
      }
    };
    fetchUniversity();
  }, [slug]);

  // Check if user has saved this university (when user or slug changes)
  useEffect(() => {
    const checkSavedStatus = async () => {
      if (!user?.user_id || !slug) {
        setIsSaved(false);
        return;
      }
      try {
        const savedRes = await fetch(`${API_URL}/api/university-pages/saved/${user.user_id}`);
        if (savedRes.ok) {
          const savedData = await savedRes.json();
          const savedSlugs = savedData.saved_universities?.map(u => u.slug) || [];
          setIsSaved(savedSlugs.includes(slug));
        }
      } catch (e) {
        console.log('Could not check saved status');
      }
    };
    checkSavedStatus();
  }, [user, slug]);

  // Update document title
  useEffect(() => {
    if (university) {
      document.title = university.meta_title || `Study at ${university.university_name} | UniXperts`;
    }
    return () => {
      document.title = 'UniXperts - Study Abroad Consultants';
    };
  }, [university]);

  // Save/Unsave university for later
  const handleSaveToggle = async () => {
    if (!user?.user_id) {
      toast.info('Please login to save universities for later');
      return;
    }
    
    setSavingState(true);
    try {
      const method = isSaved ? 'DELETE' : 'POST';
      const res = await fetch(`${API_URL}/api/university-pages/saved/${user.user_id}/${slug}`, {
        method
      });
      
      if (res.ok) {
        setIsSaved(!isSaved);
        toast.success(isSaved ? 'Removed from saved list' : 'Saved for later! View in your dashboard.');
      } else {
        toast.error('Failed to update saved list');
      }
    } catch (e) {
      console.error('Error toggling save:', e);
      toast.error('Something went wrong');
    } finally {
      setSavingState(false);
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen pt-20 flex items-center justify-center ${isDark ? 'bg-[#1a2830]' : 'bg-white'}`}>
        <Loader2 className="w-10 h-10 animate-spin text-ux-cyan" />
      </div>
    );
  }

  if (error || !university) {
    return (
      <div className={`min-h-screen pt-20 ${isDark ? 'bg-[#1a2830]' : 'bg-white'}`}>
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <h1 className={`text-3xl font-bold mb-4 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
            University Not Found
          </h1>
          <p className={`mb-8 ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
            {error || "The university you're looking for doesn't exist or hasn't been published yet."}
          </p>
          <Link to="/destinations">
            <Button className="bg-ux-cyan hover:bg-ux-cyan/90 text-white">
              <ArrowLeft className="w-4 h-4 mr-2" /> Browse All Universities
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Comprehensive JSON-LD Structured Data for SEO and AI Search
  const structuredData = university ? {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    "name": university.university_name,
    "description": university.overview,
    "url": window.location.href,
    "address": {
      "@type": "PostalAddress",
      "addressCountry": university.country,
      "addressLocality": university.city
    },
    // aggregateRating removed intentionally — the platform does NOT collect
    // real user reviews of universities, so surfacing a fabricated rating in
    // structured data would (a) mislead searchers and (b) risk a Google
    // structured-data policy violation. Re-enable ONLY when a real review
    // pipeline exists on the domain.
    ...(university.popular_programs && {
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Academic Programs",
        "itemListElement": university.popular_programs.slice(0, 5).map((program, idx) => ({
          "@type": "EducationalOccupationalProgram",
          "name": program.name,
          "programType": program.level,
          "timeToComplete": program.duration,
          ...(program.tuition_range && {
            "offers": {
              "@type": "Offer",
              "price": program.tuition_range,
              "priceCurrency": "USD"
            }
          })
        }))
      }
    })
  } : null;

  // FAQ Schema for AI Search optimization
  const faqStructuredData = university?.faqs?.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": university.faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  } : null;

  // BreadcrumbList Schema
  const breadcrumbData = university ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": window.location.origin },
      { "@type": "ListItem", "position": 2, "name": "Destinations", "item": `${window.location.origin}/destinations` },
      { "@type": "ListItem", "position": 3, "name": university.country, "item": `${window.location.origin}/destinations/${university.country?.toLowerCase()}` },
      { "@type": "ListItem", "position": 4, "name": university.university_name }
    ]
  } : null;

  return (
    <>
      {/* SEO Meta Tags */}
      {university && (
        <SEO 
          title={`${university.university_name} - Admission, Fees, Ranking | Study in ${university.country || 'Overseas'}`}
          description={`Study at ${university.university_name}${university.country ? `, ${university.country}` : ''}. QS Rank: ${university.qs_ranking || university.world_ranking || 'N/A'}. Get admission guidance from UniXperts.`}
          keywords={`${university.university_name}, ${university.university_name} admission, ${university.university_name} fees, ${university.university_name} ranking, study in ${university.country || 'abroad'}, ${university.university_name} scholarships, ${university.university_name} courses`}
          canonical={`https://unixperts.com/universities/${slug}`}
        />
      )}
      
      {/* Inject JSON-LD for SEO and AI Search */}
      {university && (
        <>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
          />
          {faqStructuredData && (
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }}
            />
          )}
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbData) }}
          />
        </>
      )}

      <div className={`min-h-screen w-full max-w-[100vw] overflow-x-hidden ${isDark ? 'bg-[#1a2830]' : 'bg-gray-50'}`} data-testid="university-detail-page">
        {/* Hero Section */}
        <section className="relative pt-20 pb-16 overflow-hidden">
          {/* Dark gradient background for better text legibility */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460]" />
          
          {/* Subtle pattern overlay */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
            }} />
          </div>
          
          {/* Accent glow effects */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-ux-cyan/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-ux-violet/20 rounded-full blur-3xl" />
          
          <div className="w-full max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 relative z-10">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-white/60 text-sm mb-6">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to="/destinations" className="hover:text-white transition-colors">Destinations</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to={`/destinations/${university.country?.toLowerCase()}`} className="hover:text-white transition-colors">{university.country}</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-white font-medium">{university.university_name}</span>
            </nav>

            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 font-display drop-shadow-lg">
                  Study at {university.university_name}
                </h1>
                
                {university.tagline && (
                  <p className="text-xl text-ux-cyan font-medium mb-6">{university.tagline}</p>
                )}
                
                <div className="flex flex-wrap items-center gap-4 mb-8">
                  <div className="flex items-center gap-2 text-white bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                    <MapPin className="w-5 h-5 text-ux-cyan" />
                    <span className="font-medium">{university.city ? `${university.city}, ` : ''}{university.country}</span>
                  </div>
                  {university.world_ranking && (
                    <div className="flex items-center gap-2 text-white bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                      <Trophy className="w-5 h-5 text-ux-amber" />
                      <span className="font-medium">World Rank #{university.world_ranking}</span>
                    </div>
                  )}
                </div>

                {/* Primary CTA */}
                <div className="flex flex-wrap gap-3">
                  <Button 
                    size="lg" 
                    className="bg-gradient-to-r from-ux-cyan to-ux-green text-white hover:opacity-90 font-semibold px-6 py-5 text-base rounded-full shadow-lg shadow-ux-cyan/30"
                    onClick={() => window.dispatchEvent(new CustomEvent('open-ai-chat', { detail: { intent: 'eligibility', universityName: university.name } }))}
                    data-testid="check-eligibility-btn"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Check My Eligibility
                  </Button>
                  <a href="https://wa.me/917273928928" target="_blank" rel="noopener noreferrer">
                    <Button 
                      size="lg" 
                      variant="outline"
                      className="border-2 border-white/50 text-white hover:bg-white hover:text-ux-charcoal px-5 py-5 text-base rounded-full backdrop-blur-sm"
                      data-testid="talk-to-expert-btn"
                    >
                      <MessageCircle className="w-4 h-4 mr-2" /> Talk to Expert
                    </Button>
                  </a>
                </div>
              </div>

              {/* Quick Stats Cards */}
              <div className="grid grid-cols-2 gap-4">
                {university.world_ranking && (
                  <Card className="bg-white/15 backdrop-blur-md border-white/20 shadow-lg">
                    <CardContent className="p-5 text-center">
                      <Trophy className="w-10 h-10 text-ux-amber mx-auto mb-2 drop-shadow-md" />
                      <p className="text-2xl sm:text-3xl font-bold text-white drop-shadow-sm">#{String(university.world_ranking).replace(/[^0-9-]/g, '')}</p>
                      <p className="text-white/80 text-sm font-medium">{t.world_ranking_label}</p>
                      <p className="text-white/50 text-xs mt-1">{t.qs_year_label}</p>
                    </CardContent>
                  </Card>
                )}
                {/* Acceptance rate removed — no verified source available */}
                {university.student_population && (
                  <Card className="bg-white/15 backdrop-blur-md border-white/20 shadow-lg">
                    <CardContent className="p-5 text-center">
                      <Users className="w-10 h-10 text-ux-green mx-auto mb-2 drop-shadow-md" />
                      <p className="text-2xl sm:text-3xl font-bold text-white drop-shadow-sm">
                        {(() => {
                          const pop = String(university.student_population);
                          const match = pop.match(/([\d,]+\+?)/);
                          return match ? match[1] : pop.split(/[(\s]/)[0];
                        })()}
                      </p>
                      <p className="text-white/80 text-sm font-medium">Students</p>
                    </CardContent>
                  </Card>
                )}
                {university.international_students && (
                  <Card className="bg-white/15 backdrop-blur-md border-white/20 shadow-lg">
                    <CardContent className="p-5 text-center">
                      <Globe className="w-10 h-10 text-ux-violet mx-auto mb-2 drop-shadow-md" />
                      <p className="text-2xl sm:text-3xl font-bold text-white drop-shadow-sm">
                        {(() => {
                          const intl = String(university.international_students);
                          const match = intl.match(/(\d+[-–]?\d*)\s*%/);
                          return match ? `${match[1]}%` : intl.split(/[(\s]/)[0];
                        })()}
                      </p>
                      <p className="text-white/80 text-sm font-medium">International</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-12 sm:py-16">
          <div className="w-full max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Main Content Column */}
              <div className="lg:col-span-2 space-y-8">
                {/* Overview */}
                {university.overview && (
                  <Card className={isDark ? 'bg-[#233842] border-white/10' : ''}>
                    <CardContent className="p-6">
                      <h2 className={`text-2xl font-bold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                        <BookOpen className="w-6 h-6 text-ux-cyan" /> {t.overview_header}
                      </h2>
                      <p className={`leading-relaxed ${isDark ? 'text-white/80' : 'text-ux-charcoal/80'}`}>
                        {university.overview}
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Why Study Here */}
                {university.why_study_here && (
                  <Card className={isDark ? 'bg-[#233842] border-white/10' : ''}>
                    <CardContent className="p-6">
                      <h2 className={`text-2xl font-bold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                        <Star className="w-6 h-6 text-ux-amber" /> {t.why_study_prefix} {university.university_name}?
                      </h2>
                      <p className={`leading-relaxed ${isDark ? 'text-white/80' : 'text-ux-charcoal/80'}`}>
                        {university.why_study_here}
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* QS Subject Rankings 2026 */}
                {university.subject_rankings?.length > 0 && (
                  <Card className={isDark ? 'bg-[#233842] border-white/10' : ''} data-testid="qs-subject-rankings-card">
                    <CardContent className="p-6">
                      <div className="flex items-baseline justify-between mb-4 gap-3 flex-wrap">
                        <h2 className={`text-2xl font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                          <Trophy className="w-6 h-6 text-amber-500" /> QS Subject Rankings
                        </h2>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-white/10 text-white/70' : 'bg-gray-100 text-ux-charcoal/70'}`}>
                          {university.subject_rankings_year || 2026}
                        </span>
                      </div>
                      <p className={`text-sm mb-4 ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
                        Highlights from the QS World University Rankings by Subject.
                      </p>
                      <div className="grid sm:grid-cols-2 gap-2">
                        {university.subject_rankings
                          .filter(s => !s.is_broad && (s.rank || s.rank_range))
                          .slice(0, 10)
                          .map((s, i) => (
                            <div
                              key={i}
                              className={`flex items-center justify-between gap-3 p-3 rounded-lg ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}
                              data-testid={`qs-subject-item-${i}`}
                            >
                              <span className={`text-sm truncate ${isDark ? 'text-white/80' : 'text-ux-charcoal/80'}`} title={s.subject}>
                                {s.subject}
                              </span>
                              <span className={`text-sm font-semibold whitespace-nowrap ${isDark ? 'text-ux-cyan' : 'text-ux-blue'}`}>
                                {s.rank ? `#${s.rank}` : s.rank_range}
                              </span>
                            </div>
                          ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Popular Programs */}
                {university.popular_programs?.length > 0 && (
                  <Card className={isDark ? 'bg-[#233842] border-white/10' : ''}>
                    <CardContent className="p-6">
                      <h2 className={`text-2xl font-bold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                        <GraduationCap className="w-6 h-6 text-ux-violet" /> {t.programs_header}
                      </h2>
                      <div className="grid sm:grid-cols-2 gap-4">
                        {university.popular_programs.map((program, i) => (
                          <div 
                            key={i} 
                            className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}
                          >
                            <h3 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                              {program.name}
                            </h3>
                            <div className={`space-y-1 text-sm ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="text-xs">{program.level}</Badge>
                              </div>
                              <div className="flex items-center gap-2">
                                <Clock className="w-3 h-3" /> {program.duration}
                              </div>
                              {program.tuition_range && (
                                <div className="flex items-center gap-2">
                                  <DollarSign className="w-3 h-3" /> {program.tuition_range}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Admission Requirements */}
                {university.admission_requirements?.length > 0 && (
                  <Card className={isDark ? 'bg-[#233842] border-white/10' : ''}>
                    <CardContent className="p-6">
                      <h2 className={`text-2xl font-bold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                        <FileText className="w-6 h-6 text-ux-coral" /> {t.requirements_header}
                      </h2>
                      <div className="space-y-4">
                        {university.admission_requirements.map((req, i) => (
                          <div key={i} className="flex items-start gap-3">
                            <CheckCircle2 className="w-5 h-5 text-ux-green mt-0.5 flex-shrink-0" />
                            <div>
                              <p className={`font-medium ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                                {req.requirement}
                              </p>
                              {req.details && (
                                <p className={`text-sm ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
                                  {req.details}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Campus Life */}
                {university.campus_life && (
                  <Card className={isDark ? 'bg-[#233842] border-white/10' : ''}>
                    <CardContent className="p-6">
                      <h2 className={`text-2xl font-bold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                        <Home className="w-6 h-6 text-ux-mint" /> {t.campus_header}
                      </h2>
                      <p className={`leading-relaxed ${isDark ? 'text-white/80' : 'text-ux-charcoal/80'}`}>
                        {university.campus_life}
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Career Outcomes */}
                {university.career_outcomes && (
                  <Card className={isDark ? 'bg-[#233842] border-white/10' : ''}>
                    <CardContent className="p-6">
                      <h2 className={`text-2xl font-bold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                        <Briefcase className="w-6 h-6 text-ux-blue" /> {t.career_header}
                      </h2>
                      <p className={`leading-relaxed ${isDark ? 'text-white/80' : 'text-ux-charcoal/80'}`}>
                        {university.career_outcomes}
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* FAQs */}
                {university.faqs?.length > 0 && (
                  <Card className={isDark ? 'bg-[#233842] border-white/10' : ''}>
                    <CardContent className="p-6">
                      <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                        {t.faq_header}
                      </h2>
                      <Accordion type="single" collapsible className="w-full">
                        {university.faqs.map((faq, i) => (
                          <AccordionItem key={i} value={`faq-${i}`} className={isDark ? 'border-white/10' : ''}>
                            <AccordionTrigger className={`text-left ${isDark ? 'text-white hover:text-ux-cyan' : 'text-ux-charcoal hover:text-ux-cyan'}`}>
                              {faq.question}
                            </AccordionTrigger>
                            <AccordionContent className={isDark ? 'text-white/70' : 'text-ux-charcoal/70'}>
                              {faq.answer}
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Intake Months - First for better visibility */}
                {university.intake_months?.length > 0 && (
                  <Card className={isDark ? 'bg-[#233842] border-white/10' : 'shadow-md'}>
                    <CardContent className="p-6">
                      <h3 className={`text-xl font-bold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                        <Calendar className="w-5 h-5 text-ux-violet" /> Intake Months
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {university.intake_months.map((month, i) => (
                          <Badge key={i} variant="outline" className={`py-1.5 px-3 ${isDark ? 'border-white/30 text-white' : 'border-ux-charcoal/30'}`}>
                            {month}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Cost Calculator Card */}
                {university.cost_breakdown && (
                  <Card className={`sticky top-24 ${isDark ? 'bg-[#233842] border-white/10' : 'shadow-md'}`}>
                    <CardContent className="p-6">
                      <h3 className={`text-xl font-bold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                        <DollarSign className="w-5 h-5 text-ux-green" /> Estimated Costs
                      </h3>
                      <div className="space-y-4">
                        <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                          <p className={`text-sm ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>{t.annual_tuition_label}</p>
                          <p className={`text-xl font-bold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                            ${university.cost_breakdown.tuition_min?.toLocaleString()} - ${university.cost_breakdown.tuition_max?.toLocaleString()}
                          </p>
                        </div>
                        <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                          <p className={`text-sm ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>Monthly Living Cost</p>
                          <p className={`text-xl font-bold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                            ${university.cost_breakdown.living_cost_monthly?.toLocaleString()}/month
                          </p>
                        </div>
                        <Link to="/contact">
                          <Button className="w-full bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 text-white font-semibold rounded-full shadow-md">
                            Talk to a Loan Advisor
                          </Button>
                        </Link>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Lead Capture Form - Desktop Sidebar */}
                <div className="hidden lg:block">
                  <LeadCaptureForm 
                    variant="compact"
                    showHeader={true}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Related Universities Section - SEO Internal Linking */}
        {relatedUniversities.length > 0 && (
          <section className={`py-16 ${isDark ? 'bg-[#0f1a1f]' : 'bg-white'}`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-10">
                <h2 className={`text-2xl sm:text-3xl font-bold mb-3 ${isDark ? 'text-white' : 'text-ux-charcoal'}`} data-testid="university-related-header">
                  {(t.related_header_prefix || 'Explore Similar Universities in')} {university.country}
                </h2>
                <p className={`${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
                  {t.related_subtitle || 'Discover more top-ranked institutions with similar programs'}
                </p>
              </div>
              
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedUniversities.map((related) => (
                  <Link 
                    key={related.slug} 
                    to={`/university/${related.slug}`}
                    className="group"
                  >
                    <Card className={`h-full transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${
                      isDark 
                        ? 'bg-white/5 border-white/10 hover:bg-white/10' 
                        : 'bg-white border-gray-200 hover:border-ux-cyan/30'
                    }`}>
                      <CardContent className="p-5">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <h3 className={`font-semibold text-lg mb-1 group-hover:text-ux-cyan transition-colors ${
                              isDark ? 'text-white' : 'text-ux-charcoal'
                            }`}>
                              {related.university_name}
                            </h3>
                            <div className="flex items-center gap-1 text-sm">
                              <MapPin className={`w-3.5 h-3.5 ${isDark ? 'text-white/40' : 'text-ux-charcoal/40'}`} />
                              <span className={isDark ? 'text-white/60' : 'text-ux-charcoal/60'}>
                                {related.city}, {related.country}
                              </span>
                            </div>
                          </div>
                          {related.world_ranking && (
                            <Badge variant="secondary" className="bg-ux-amber/10 text-ux-amber border-0 shrink-0">
                              #{related.world_ranking}
                            </Badge>
                          )}
                        </div>
                        
                        {related.hero_tagline && (
                          <p className={`text-sm line-clamp-2 mb-3 ${isDark ? 'text-white/50' : 'text-ux-charcoal/60'}`}>
                            {related.hero_tagline}
                          </p>
                        )}
                        
                        <div className="flex items-center justify-between mt-auto pt-3 border-t border-dashed ${isDark ? 'border-white/10' : 'border-gray-200'}">
                          <span className={`text-sm font-medium group-hover:translate-x-1 transition-transform flex items-center gap-1 ml-auto ${
                            isDark ? 'text-ux-cyan' : 'text-ux-blue'
                          }`}>
                            View Details <ExternalLink className="w-3.5 h-3.5" />
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
              
              <div className="text-center mt-8">
                <Link to={`/destinations/${university.country?.toLowerCase()}`}>
                  <Button 
                    variant="outline" 
                    className={`rounded-full px-8 ${isDark ? 'border-white/30 text-white hover:bg-white/10' : ''}`}
                  >
                    View All Universities in {university.country} <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </div>
          </section>
        )}

        {/* Mobile Lead Form Section - Full Width with Better Separation */}
        <section className={`lg:hidden py-12 ${isDark ? 'bg-[#0f1a1f]' : 'bg-gradient-to-b from-gray-100 to-gray-50'}`}>
          <div className="max-w-lg mx-auto px-4">
            <div className="text-center mb-6">
              <h2 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                Start Your Journey Today
              </h2>
              <p className={`${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
                Get personalized guidance from our expert counselors
              </p>
            </div>
            <LeadCaptureForm 
              variant="compact"
              showHeader={false}
            />
          </div>
        </section>

        {/* Sticky Bottom CTA (Mobile) */}
        <div className={`fixed bottom-0 left-0 right-0 p-4 ${isDark ? 'bg-[#1a2830]' : 'bg-white'} border-t ${isDark ? 'border-white/10' : 'border-gray-200'} lg:hidden z-40`}>
          <div className="flex gap-3 max-w-lg mx-auto">
            <Button
              className="flex-1 bg-gradient-to-r from-ux-cyan to-ux-blue hover:opacity-90 text-white rounded-full font-semibold"
              onClick={() => window.dispatchEvent(new CustomEvent('open-ai-chat', { detail: { intent: 'eligibility', universityName: university.name } }))}
              data-testid="mobile-check-eligibility-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Check Eligibility
            </Button>
            <a href="tel:+919636957799" className="flex-1">
              <Button 
                variant="outline"
                className={`w-full rounded-full font-semibold ${isDark ? 'border-white/30 text-white hover:bg-white/10' : ''}`}
              >
                <Phone className="w-4 h-4 mr-2" /> Call Us
              </Button>
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default UniversityDetailPage;
